import {
  Box,
  Button,
  Flex,
  FormControl, FormErrorMessage, FormLabel,
  HStack,
  Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Select,
  Switch,
  Tag,
  TableContainer, Table, Thead, Tbody, Tr, Th, Td,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Icon,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HelpIcon from '../InvestmentScreening/HelpIcon';

import FilterTextBox from '../../components/FilterTextBox';
import FilterDropdown from '../../components/FilterDropdown';
import OrgChartSelectModal from './OrgChartSelectModal';
import OrgViewAccessPanel, { OrgViewAccessPanelHandle } from './OrgViewAccessPanel';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, UserRole, getUserRoleNameByCode } from '../../contexts/constants';
import { Errors, OrgViewAccessItemType, OrganizationWithHierarchyType, SystemAccessUserAffiliationType, SystemAccessUserType } from '../../contexts/types';
import { WarningTwoIcon } from '@chakra-ui/icons/WarningTwo';

/** 所属部署フィルターの「未登録組織」選択時の内部値 */
const UNREGISTERED_AFFILIATION_FILTER_VALUE = "__UNREGISTERED__";

/**
 * マスタメンテナンス - システム利用設定 画面
 */
const SystemAccessSetting: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  const TITLE = t('features.MasterMaintenance.SystemAccessSetting.title');
  const ENTITY = t('features.MasterMaintenance.SystemAccessSetting.entityLabel');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル（1列目：ユーザーID） */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
  }

  /** 列固定スタイル（2列目：ユーザー名） */
  const fixedCol2CellStyle = {
    position: "sticky" as const,
    left: "300px",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 220,
  }

  /** ヘルプ 値 */
  const helpDtStyle = {
    fontWeight: "bold",
  }
  /** ヘルプ 説明 */
  const helpDdStyle = {
    paddingLeft: "10px",
  }

  /** ユーザー一覧データ */
  const [masterData, setMasterData] = useState<SystemAccessUserType[]>([]);

  /** 組織一覧（OrgViewAccessPanel の組織図モーダル用） */
  const [orgList, setOrgList] = useState<OrganizationWithHierarchyType[]>([]);

  /** 編集対象ユーザーの許可済み組織一覧 */
  const [committedOrgList, setCommittedOrgList] = useState<OrgViewAccessItemType[]>([]);

  /** ユーザー所属組織のルートから全子孫組織一覧 */
  const [visibleOrgIds, setVisibleOrgIds] = useState<string[]>([]);

  /** 許可済み組織一覧の読込中フラグ */
  const [isOrgAccessLoading, setIsOrgAccessLoading] = useState(false);

  /** 組織閲覧許可パネルへの ref（保存時に save() を呼ぶ） */
  const orgViewAccessPanelRef = useRef<OrgViewAccessPanelHandle>(null);

  /** fetchCommittedOrgList の競合防止: 直近のフェッチ対象ユーザーID */
  const currentFetchTargetRef = useRef<string | null>(null);

  /** 編集対象ユーザー */
  const [editTarget, setEditTarget] = useState<SystemAccessUserType>({
    userId: "",
    userName: "",
    isSystemAccessible: false,
    userRole: null,
    isMasterMaintenanceAccessible: false,
    isHeadquartersManagement: false,
    isPreReviewAssignee: false,
    isHqReviewAssignee: false,
    isPresidentReportAssignee: false,
  });

  /** モーダル表示制御 */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  /** フィルター：ユーザーID */
  const [filterUserId, setFilterUserId] = useState<string>("");

  /** フィルター：ユーザー名 */
  const [filterUserName, setFilterUserName] = useState<string>("");

  /** フィルター：所属部署 */
  const [filterAffiliation, setFilterAffiliation] = useState<string>("");

  /** 所属部署ツリーモーダル表示制御 */
  const { isOpen: isOpenAffiliationModal, onOpen: onOpenAffiliationModal, onClose: onCloseAffiliationModal } = useDisclosure();

  /** 所属部署ツリーモーダルの表示対象ユーザー */
  const [affiliationModalTarget, setAffiliationModalTarget] = useState<SystemAccessUserAffiliationType[]>([]);

  /** フィルター：システム利用可否 */
  const [filterAccessible, setFilterAccessible] = useState<any>("");

  /** フィルター：権限 */
  const [filterUserRole, setFilterUserRole] = useState<any>("");

  /** フィルター：マスタメンテナンス表示 */
  const [filterMasterMaintenance, setFilterMasterMaintenance] = useState<any>("");

  /** フィルター：本社経営管理 */
  const [filterHeadquartersManagement, setFilterHeadquartersManagement] = useState<any>("");

  /** フィルター：プレ検討会担当者 */
  const [filterPreReviewAssignee, setFilterPreReviewAssignee] = useState<any>("");

  /** フィルター：本部長設備投資委員会担当者 */
  const [filterHqReviewAssignee, setFilterHqReviewAssignee] = useState<any>("");

  /** フィルター：会長/副会長/社長 確認担当者 */
  const [filterPresidentReportAssignee, setFilterPresidentReportAssignee] = useState<any>("");

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** 保存中フラグ */
  const [isSaving, setIsSaving] = useState(false);

  /** 所属部署フィルター用データソース（FilterDropdown はpropNameの一意な値を抽出するため、部署名を1件ずつ持つ配列に変換。未登録ユーザーがいる場合は「未登録組織」も候補に追加） */
  const affiliationFilterDataSource: { affiliationName: string }[] = [
    ...(masterData ?? []).flatMap(m => (m.affiliations ?? []).map(a => ({ affiliationName: a.organizationName }))),
    ...((masterData ?? []).some(m => (m.affiliations ?? []).length === 0) ? [{ affiliationName: UNREGISTERED_AFFILIATION_FILTER_VALUE }] : []),
  ];

  /** フィルター済みデータ */
  const filteredMasterData = (masterData ?? []).filter(m => {
    if (!m) return false;

    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    // システム利用可否フィルター
    if (filterAccessible !== "") {
      if (m.isSystemAccessible !== filterAccessible) return false;
    }

    // 権限フィルター
    if (filterUserRole !== "") {
      if ((m.userRole ?? "") !== filterUserRole) return false;
    }

    // マスタメンテナンス表示フィルター
    if (filterMasterMaintenance !== "") {
      if (m.isMasterMaintenanceAccessible !== filterMasterMaintenance) return false;
    }

    // 本社経営管理フィルター
    if (filterHeadquartersManagement !== "") {
      if (m.isHeadquartersManagement !== filterHeadquartersManagement) return false;
    }

    // プレ検討会担当者フィルター
    if (filterPreReviewAssignee !== "") {
      if (m.isPreReviewAssignee !== filterPreReviewAssignee) return false;
    }

    // 本部長設備投資委員会担当者フィルター
    if (filterHqReviewAssignee !== "") {
      if (m.isHqReviewAssignee !== filterHqReviewAssignee) return false;
    }

    // 会長/副会長/社長 確認担当者フィルター
    if (filterPresidentReportAssignee !== "") {
      if (m.isPresidentReportAssignee !== filterPresidentReportAssignee) return false;
    }

    // 所属部署フィルター（複数所属時はいずれか1件でも一致すればOK。未登録組織の絞り込みにも対応）
    if (filterAffiliation !== "") {
      if (filterAffiliation === UNREGISTERED_AFFILIATION_FILTER_VALUE) {
        if ((m.affiliations ?? []).length > 0) return false;
      } else {
        const affiliationNames = (m.affiliations ?? []).map(a => a.organizationName);
        if (!affiliationNames.includes(filterAffiliation)) return false;
      }
    }

    return (
      hasFilterString(m.userId, filterUserId) &&
      hasFilterString(m.userName, filterUserName)
    );
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await fetchMasterData();
      await fetchOrgList();
    }
    fetch();
  }, []);

  /**
   * ユーザー一覧取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
            query {
              getSystemAccessUsers {
                userId
                userName
                isSystemAccessible
                userRole
                isMasterMaintenanceAccessible
                isHeadquartersManagement
                isPreReviewAssignee
                isHqReviewAssignee
                isPresidentReportAssignee
                createUserName
                createDate
                updateUserName
                updateDate
                affiliations {
                  organizationId
                  organizationName
                  deleted
                }
              }
            }`
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getSystemAccessUsers) {
        setMasterData(response.data.getSystemAccessUsers);
      } else {
        console.error(`Failed to call API getSystemAccessUsers. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `ユーザー一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 組織一覧取得（OrgViewAccessPanel のドロップダウン用、マウント時1回のみ）
   */
  const fetchOrgList = async () => {
    try {
      const result = await WebAPI.request({
        query: `
          query {
            getOrganizationWithHierarchy {
              organizationId
              organizationName
              parentOrganizationId
            }
          }
        `
      });
      if (result?.data?.getOrganizationWithHierarchy) {
        setOrgList(result.data.getOrganizationWithHierarchy);
      }
    } catch (error) {
      console.error('Failed. fetchOrgList', error);
    }
  }

  /**
   * 所属部署一覧の代表表示テキストを生成する
   * 未登録の場合は「未登録組織」、削除済み組織を含む場合は「削除済み組織」を表示し、
   * 複数所属の場合は代表1件＋残り件数を表示する
   * @param affiliations 所属部署一覧
   * @returns 一覧セルに表示するテキスト
   */
  const showAffiliationDisplayText = (affiliations: SystemAccessUserAffiliationType[] | undefined) => {
    if (!affiliations || affiliations.length === 0) {
      // 組織が未登録
      return (
        <Text isTruncated maxW={180} title={t('features.MasterMaintenance.SystemAccessSetting.label.affiliationUnregistered')}>
          <Icon as={WarningTwoIcon} marginBottom={1} color="yellow.500" mr={1} />{t('features.MasterMaintenance.SystemAccessSetting.label.affiliationUnregistered')}
        </Text>
      );
    }

    return affiliations?.map((affiliation: SystemAccessUserAffiliationType, idx) => {
      // 削除済みかの組織判定
      if (affiliation.deleted) {
        return (
          <Text isTruncated maxW={180} title={t('features.MasterMaintenance.SystemAccessSetting.label.affiliationDeleted')}>
            <Icon as={WarningTwoIcon} marginBottom={1} color="red.400" mr={1} />{affiliation.organizationName}
          </Text>
        );
      }
      else {
        return (
          <Button
            variant="link"
            textDecoration="underline"
            color="blue.500"
            onClick={(e) => {
              e.currentTarget.blur();
              handleOpenAffiliationModal(affiliations);
            }}
            sx={{
              display: 'inline-block',
              maxW: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {affiliation.organizationName}
          </Button>
        );
      }
    });
  }


  /**
   * 所属部署ツリーモーダルを表示する（対象ユーザーの所属部署のみハイライト）
   * @param affiliations 対象ユーザーの所属部署一覧
   */
  const handleOpenAffiliationModal = (affiliations: SystemAccessUserAffiliationType[] | undefined) => {
    setAffiliationModalTarget(affiliations ?? []);
    onOpenAffiliationModal();
  }

  /** 所属部署ツリーモーダルにハイライト表示する組織ID一覧（対象ユーザーの所属部署のみ、子孫・祖先は含めない） */
  const affiliationModalHighlightIds = affiliationModalTarget
    .map(a => a.organizationId)
    .filter(id => orgList.some(o => o.organizationId === id));

  /**
   * 編集対象ユーザーの許可済み組織一覧を取得（モーダルオープン時に呼ぶ）
   */
  const fetchCommittedOrgList = async (userId: string) => {
    setIsOrgAccessLoading(true);
    try {
      const result = await WebAPI.request({
        query: `
        query getOrgViewAccess($userId: String!) {
          getOrgViewAccess(userId: $userId) {
            orgViewAccessList { userId organizationId organizationName memo createDate }
            visibleOrgIds
          }
        }
      `,
        variables: { userId },
      });
      if (currentFetchTargetRef.current === userId) {
        setCommittedOrgList(result?.data?.getOrgViewAccess?.orgViewAccessList ?? []);
        setVisibleOrgIds(result?.data?.getOrgViewAccess?.visibleOrgIds ?? []);
      }
    } catch (error) {
      console.error('Failed. fetchCommittedOrgList', error);
      if (currentFetchTargetRef.current === userId) {
        setCommittedOrgList([]);
        setVisibleOrgIds([]);
      }
    } finally {
      setIsOrgAccessLoading(false);
    }
  };

  /**
   * 行クリック → モーダル表示
   */
  const handleEdit = (user: SystemAccessUserType) => {
    setEditTarget({ ...user });
    setCommittedOrgList([]);
    currentFetchTargetRef.current = user.userId;
    onOpenEditModal();
    fetchCommittedOrgList(user.userId);
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      setIsSaving(false);
      return;
    }

    if (!await putSystemAccessSetting()) {
      setIsSaving(false);
      return;
    }

    if (orgViewAccessPanelRef.current) {
      if (!await orgViewAccessPanelRef.current.save()) {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.OrgViewAccessPanel.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setIsSaving(false);
        return;
      }
    }

    await fetchMasterData();

    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェック
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});
    const localErrors: Errors = {};
    let result = true;

    if (!editTarget.userRole) {
      localErrors.userRole = {
        isError: true,
        errorMessage: t('features.MasterMaintenance.SystemAccessSetting.error.roleRequired'),
      };
      result = false;
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * システム利用設定を更新
   */
  const putSystemAccessSetting = async () => {
    try {
      const input = {
        userId: editTarget.userId,
        userRole: editTarget.userRole || null,
        isMasterMaintenanceAccessible: editTarget.isMasterMaintenanceAccessible ?? false,
        isHeadquartersManagement: editTarget.isHeadquartersManagement ?? false,
        isPreReviewAssignee: editTarget.isPreReviewAssignee ?? false,
        isHqReviewAssignee: editTarget.isHqReviewAssignee ?? false,
        isPresidentReportAssignee: editTarget.isPresidentReportAssignee ?? false,
        updateDate: editTarget.updateDate,
      };

      const result = await WebAPI.request({
        query: `
            mutation putSystemAccessSetting($input: SystemAccessSettingInput!) {
              putSystemAccessSetting(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      if (result.data.putSystemAccessSetting.status === "success") {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.SystemAccessSetting.message.settingSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return true;
      } else if (result?.data?.putSystemAccessSetting.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn('Conflict error. putSystemAccessSetting.');
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putSystemAccessSetting. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putSystemAccessSetting. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    setEditTarget({
      userId: "",
      userName: "",
      isSystemAccessible: false,
      userRole: null,
      isMasterMaintenanceAccessible: false,
      isHeadquartersManagement: false,
      isPreReviewAssignee: false,
      isHqReviewAssignee: false,
      isPresidentReportAssignee: false,
    });
    setErrors({});
    setCommittedOrgList([]);
    setVisibleOrgIds([]);
    setIsOrgAccessLoading(false);
    currentFetchTargetRef.current = null;
  }

  return (
    <>
      <Box h="100%" mt="16px">
        <TableContainer overflowX="auto" overflowY="auto" w="100%" h="calc(100dvh - 120px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={300} />
              <col width={150} />
              <col width={220} />
              <col width={200} />
              <col width={150} />
              <col width={240} />
              <col width={200} />
              <col width={210} />
              <col width={300} />
              <col width={280} />
              <col width={150} />
              <col width={200} />
              <col width={150} />
              <col width={200} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedColCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.userId')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterUserId} setFilterValue={setFilterUserId} />
                </Th>
                <Th {...headerCellStyle} {...fixedCol2CellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.userName')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterUserName} setFilterValue={setFilterUserName} />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.affiliation')}
                  <FilterDropdown
                    filterValue={filterAffiliation}
                    setFilterValue={setFilterAffiliation}
                    dataSource={affiliationFilterDataSource}
                    propName="affiliationName"
                    displayNameFunc={(value: string) => value === UNREGISTERED_AFFILIATION_FILTER_VALUE ? t('features.MasterMaintenance.SystemAccessSetting.label.affiliationUnregistered') : value}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.systemAccessible')}
                  <FilterDropdown
                    filterValue={filterAccessible}
                    setFilterValue={setFilterAccessible}
                    dataSource={masterData}
                    propName="isSystemAccessible"
                    displayNameFunc={(value: boolean) => value ? t('features.MasterMaintenance.SystemAccessSetting.label.accessAvailable') : t('features.MasterMaintenance.SystemAccessSetting.label.accessNotAvailable')}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.userRole')}
                  <FilterDropdown
                    filterValue={filterUserRole}
                    setFilterValue={setFilterUserRole}
                    dataSource={masterData}
                    propName="userRole"
                    displayNameFunc={(value: string) => getUserRoleNameByCode(value) || t('features.MasterMaintenance.SystemAccessSetting.label.notSet')}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.masterMaintenanceAccessible')}
                  <FilterDropdown
                    filterValue={filterMasterMaintenance}
                    setFilterValue={setFilterMasterMaintenance}
                    dataSource={masterData}
                    propName="isMasterMaintenanceAccessible"
                    displayNameFunc={(value: boolean) => value ? t('features.MasterMaintenance.SystemAccessSetting.label.displayed') : t('features.MasterMaintenance.SystemAccessSetting.label.hidden')}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.headquartersManagement')}
                  <FilterDropdown
                    filterValue={filterHeadquartersManagement}
                    setFilterValue={setFilterHeadquartersManagement}
                    dataSource={masterData}
                    propName="isHeadquartersManagement"
                    displayNameFunc={(value: boolean) => value ? "ON" : "OFF"}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.preReviewAssignee')}
                  <FilterDropdown
                    filterValue={filterPreReviewAssignee}
                    setFilterValue={setFilterPreReviewAssignee}
                    dataSource={masterData}
                    propName="isPreReviewAssignee"
                    displayNameFunc={(value: boolean) => value ? "ON" : "OFF"}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.hqReviewAssignee')}
                  <FilterDropdown
                    filterValue={filterHqReviewAssignee}
                    setFilterValue={setFilterHqReviewAssignee}
                    dataSource={masterData}
                    propName="isHqReviewAssignee"
                    displayNameFunc={(value: boolean) => value ? "ON" : "OFF"}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.SystemAccessSetting.field.presidentReportAssignee')}
                  <FilterDropdown
                    filterValue={filterPresidentReportAssignee}
                    setFilterValue={setFilterPresidentReportAssignee}
                    dataSource={masterData}
                    propName="isPresidentReportAssignee"
                    displayNameFunc={(value: boolean) => value ? "ON" : "OFF"}
                    sortFunc={(a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1)}
                  />
                </Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.SystemAccessSetting.field.registeredBy')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.SystemAccessSetting.field.registeredDate')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.SystemAccessSetting.field.updatedBy')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.SystemAccessSetting.field.updatedDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                return (
                  <Tr role="group" key={record.userId} _hover={{ bg: "gray.200" }}>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} {...fixedColCellStyle}>
                      <Button
                        variant="link"
                        textDecoration="underline"
                        color="blue.500"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          handleEdit(record);
                        }}
                        sx={{
                          display: 'inline-block',
                          maxW: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {record.userId}
                      </Button>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} {...fixedCol2CellStyle} whiteSpace="normal">{record.userName}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle}>
                      <VStack align="start" gap={0}>
                        {showAffiliationDisplayText(record.affiliations)}
                      </VStack>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      <Tag size="lg" colorScheme={record.isSystemAccessible ? 'green' : 'red'}>{record.isSystemAccessible ? t('features.MasterMaintenance.SystemAccessSetting.label.accessAvailable') : t('features.MasterMaintenance.SystemAccessSetting.label.accessNotAvailable')}</Tag>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle}>{getUserRoleNameByCode(record.userRole)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      {record.isMasterMaintenanceAccessible ? (
                        <Tag size="lg" colorScheme='green'>{t('features.MasterMaintenance.SystemAccessSetting.label.displayed')}</Tag>
                      ) : (
                        <Tag size="lg" colorScheme='gray'>{t('features.MasterMaintenance.SystemAccessSetting.label.hidden')}</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      {record.isHeadquartersManagement ? (
                        <Tag size="lg" colorScheme='blue'>ON</Tag>
                      ) : (
                        <Tag size="lg" colorScheme='gray'>OFF</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      {record.isPreReviewAssignee ? (
                        <Tag size="lg" colorScheme='blue'>ON</Tag>
                      ) : (
                        <Tag size="lg" colorScheme='gray'>OFF</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      {record.isHqReviewAssignee ? (
                        <Tag size="lg" colorScheme='blue'>ON</Tag>
                      ) : (
                        <Tag size="lg" colorScheme='gray'>OFF</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} textAlign="center">
                      {record.isPresidentReportAssignee ? (
                        <Tag size="lg" colorScheme='blue'>ON</Tag>
                      ) : (
                        <Tag size="lg" colorScheme='gray'>OFF</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} whiteSpace="normal">{record.createUserName}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle}>{record.createDate}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} whiteSpace="normal">{record.updateUserName}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle}>{record.updateDate}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('features.MasterMaintenance.SystemAccessSetting.modal.editTitle')}</Text>
          </ModalHeader>
          <ModalBody>
            <VStack>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.userId')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.userId}</Text>
                </Flex>
              </FormControl>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.userName')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.userName}</Text>
                </Flex>
              </FormControl>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.systemAccessible')}
                  </FormLabel>
                  <Box mt="8px">
                    <Tag size="lg" colorScheme={editTarget.isSystemAccessible ? 'green' : 'red'}>
                      {editTarget.isSystemAccessible
                        ? t('features.MasterMaintenance.SystemAccessSetting.label.accessAvailable')
                        : t('features.MasterMaintenance.SystemAccessSetting.label.accessNotAvailable')}
                    </Tag>
                  </Box>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.affiliation')}
                  </FormLabel>
                  <VStack align="start" gap={0}>
                    {showAffiliationDisplayText(editTarget.affiliations)}
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70} isInvalid={errors.userRole?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.userRole')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="620px" header={t('features.MasterMaintenance.SystemAccessSetting.help.role.header')}>
                      <dl>
                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.general')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.generalDesc')}</dd>

                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.business')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.businessDesc')}</dd>

                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.company')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.companyDesc')}</dd>

                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.designated')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.role.designatedDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <Select
                      value={editTarget.userRole ?? ""}
                      onChange={(event) => {
                        setEditTarget(prev => ({ ...prev, userRole: event.target.value || null }));
                      }}
                      placeholder={t('common.placeholder.selectLabel')}
                      width={300}
                      isInvalid={errors.userRole?.isError}
                    >
                      {Object.values(UserRole).map(role => (
                        <option key={role.code} value={role.code}>{role.name}</option>
                      ))}
                    </Select>
                    <FormErrorMessage mt={0}>{errors.userRole?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {editTarget.userRole === UserRole.DesignatedOrgView.code && (
                <FormControl>
                  <OrgViewAccessPanel
                    ref={orgViewAccessPanelRef}
                    userId={editTarget.userId}
                    orgList={orgList}
                    committedOrgList={committedOrgList}
                    visibleOrgIds={visibleOrgIds}
                    isLoading={isOrgAccessLoading}
                  />
                </FormControl>
              )}

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle} htmlFor="">
                    {t('features.MasterMaintenance.SystemAccessSetting.field.masterMaintenanceAccessible')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="380px" header={t('features.MasterMaintenance.SystemAccessSetting.help.masterMaintenance.header')}>
                      <dl>
                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.masterMaintenance.displayed')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.masterMaintenance.displayedDesc')}</dd>

                        <dt style={helpDtStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.masterMaintenance.hidden')}</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.masterMaintenance.hiddenDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isMasterMaintenanceAccessible: false }));
                        }}>{t('features.MasterMaintenance.SystemAccessSetting.label.hidden')}</Button>

                      <Switch
                        isChecked={editTarget.isMasterMaintenanceAccessible ?? false}
                        onChange={(event) => {
                          setEditTarget(prev => ({ ...prev, isMasterMaintenanceAccessible: event.target.checked }));
                        }}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isMasterMaintenanceAccessible: true }));
                        }}>{t('features.MasterMaintenance.SystemAccessSetting.label.displayed')}</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle} htmlFor="">
                    {t('features.MasterMaintenance.SystemAccessSetting.field.headquartersManagement')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="380px" header={t('features.MasterMaintenance.SystemAccessSetting.help.headquartersManagement.header')}>
                      <dl>
                        <dt style={helpDtStyle}>ON：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.headquartersManagement.onDesc')}</dd>

                        <dt style={helpDtStyle}>OFF：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.headquartersManagement.offDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isHeadquartersManagement: false }));
                        }}>OFF</Button>

                      <Switch
                        isChecked={editTarget.isHeadquartersManagement ?? false}
                        onChange={(event) => {
                          setEditTarget(prev => ({ ...prev, isHeadquartersManagement: event.target.checked }));
                        }}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isHeadquartersManagement: true }));
                        }}>ON</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle} htmlFor="">
                    {t('features.MasterMaintenance.SystemAccessSetting.field.preReviewAssignee')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="380px" header={t('features.MasterMaintenance.SystemAccessSetting.help.preReviewAssignee.header')}>
                      <dl>
                        <dt style={helpDtStyle}>ON：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.preReviewAssignee.onDesc')}</dd>
                        <dt style={helpDtStyle}>OFF：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.preReviewAssignee.offDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isPreReviewAssignee: false }));
                        }}>OFF</Button>
                      <Switch
                        isChecked={editTarget.isPreReviewAssignee ?? false}
                        onChange={(event) => {
                          setEditTarget(prev => ({ ...prev, isPreReviewAssignee: event.target.checked }));
                        }}
                      />
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isPreReviewAssignee: true }));
                        }}>ON</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle} htmlFor="">
                    {t('features.MasterMaintenance.SystemAccessSetting.field.hqReviewAssignee')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="380px" header={t('features.MasterMaintenance.SystemAccessSetting.help.hqReviewAssignee.header')}>
                      <dl>
                        <dt style={helpDtStyle}>ON：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.hqReviewAssignee.onDesc')}</dd>
                        <dt style={helpDtStyle}>OFF：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.hqReviewAssignee.offDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isHqReviewAssignee: false }));
                        }}>OFF</Button>
                      <Switch
                        isChecked={editTarget.isHqReviewAssignee ?? false}
                        onChange={(event) => {
                          setEditTarget(prev => ({ ...prev, isHqReviewAssignee: event.target.checked }));
                        }}
                      />
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isHqReviewAssignee: true }));
                        }}>ON</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle} htmlFor="">
                    {t('features.MasterMaintenance.SystemAccessSetting.field.presidentReportAssignee')}
                  </FormLabel>
                  <Box mr="8px" mt="8px">
                    <HelpIcon size="18px" placement="right" width="380px" header={t('features.MasterMaintenance.SystemAccessSetting.help.presidentReportAssignee.header')}>
                      <dl>
                        <dt style={helpDtStyle}>ON：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.presidentReportAssignee.onDesc')}</dd>
                        <dt style={helpDtStyle}>OFF：</dt>
                        <dd style={helpDdStyle}>{t('features.MasterMaintenance.SystemAccessSetting.help.presidentReportAssignee.offDesc')}</dd>
                      </dl>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isPresidentReportAssignee: false }));
                        }}>OFF</Button>
                      <Switch
                        isChecked={editTarget.isPresidentReportAssignee ?? false}
                        onChange={(event) => {
                          setEditTarget(prev => ({ ...prev, isPresidentReportAssignee: event.target.checked }));
                        }}
                      />
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          setEditTarget(prev => ({ ...prev, isPresidentReportAssignee: true }));
                        }}>ON</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.registeredBy')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.createUserName}</Text>
                </Flex>
              </FormControl>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.registeredDate')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.createDate}</Text>
                </Flex>
              </FormControl>

              <FormControl h={50}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.updatedBy')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.updateUserName}</Text>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.SystemAccessSetting.field.updatedDate')}
                  </FormLabel>
                  <Text mt="8px">{editTarget.updateDate}</Text>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 所属部署ツリーモーダル（表示専用） */}
      <OrgChartSelectModal
        isOpen={isOpenAffiliationModal}
        onClose={onCloseAffiliationModal}
        orgList={orgList}
        addedIds={affiliationModalHighlightIds}
        readOnly
        scopeToAddedIds
        titleKey="features.MasterMaintenance.SystemAccessSetting.modal.affiliationChart.title"
      />
    </>
  );
}

export default SystemAccessSetting;
